import SpinLoading from '../spinLoading'
import UploadImage from '../uploadImage'
import { canvasPreview } from './canvasPreview'
import {
  DeleteOutlined,
  SaveOutlined,
  ScissorOutlined,
  UploadOutlined
} from '@ant-design/icons'
import { Button, Tooltip } from 'antd'
import { t } from 'i18next'
import { IImageUpload } from 'interfaces'
import React, { ReactNode, useRef, useState } from 'react'
import ReactCrop, {
  Crop,
  PixelCrop,
  centerCrop,
  makeAspectCrop
} from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import { useFileService } from 'services'
import styled from 'styled-components'

const StyledContainer = styled.div`
  width: 100%;
  .anticon-upload svg {
    width: 20px;
    height: 20px;
  }
  .ReactCrop {
    width: 100%;
  }
  .canvas-display-none {
    width: 0 !important;
    height: 0 !important;
    display: none;
  }
  input[disabled] {
    opacity: 0.5;
  }
`

const StyledButton = styled(Tooltip)`
  margin-left: 8px;
  margin-bottom: 8px;
`
const StyledButtonContainer = styled(Tooltip)`
  display: flex;
  flex-direction: row;
  justify-content: end;
`
const StyledWrapperPreviewImg = styled(Tooltip)`
  display: flex;
  flex-direction: column;
  canvas {
    width: 100% !important;
    border-color: var(--color-gray-102) !important;
    border-radius: 4px;
  }
`
const StyledButtonInputImg = styled.button`
  width: 104px;
  height: 104px;
  border-color: none !important;
  padding: 0 !important;
  border-color: transparent;
  border-radius: 4px;
  cursor: pointer !important;
  background: var(--color-gray-102);
`
const StyledLabel = styled.div`
  color: var(--color-gray-111);
  font-weight: 600;
  font-size: 1.4rem;
  margin-bottom: 6px;
  span {
    color: var(--color-red-107);
  }
`

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: mediaWidth,
        height: mediaHeight
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  )
}

interface IProps {
  onChange?: (url?: string, img?: IImageUpload) => void
  value?: string
  className?: string
  title?: string | ReactNode
  required?: boolean
  buttonUpload?: ReactNode
  preventRemove?: boolean
  disabled?: boolean
  aspect?: number
  feeAspect?: boolean
}

export default function UploadImageCrop(props: IProps) {
  const {
    onChange,
    value,
    className,
    title,
    required,
    buttonUpload,
    preventRemove,
    disabled,
    aspect
  } = props
  const [imgSrc, setImgSrc] = useState('')
  const [cropEnable, setCropEnable] = useState(true)
  const previewCanvasRef = useRef<HTMLCanvasElement>(null)
  const inputImgRef = useRef<any>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const { postFilesToServer } = useFileService()
  const [crop, setCrop] = useState<Crop>()
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>({
    unit: 'px',
    x: 42.325000000000074,
    y: 24.200000000000003,
    width: 761.8499999999999,
    height: 93.6
  })
  const [isRemove, setIsRemove] = useState(false)
  const [loading, setLoading] = useState(false)

  function onSelectFile(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      setCrop(undefined)
      const reader = new FileReader()
      reader.addEventListener('load', () =>
        setImgSrc(reader.result?.toString() || '')
      )
      reader.readAsDataURL(e.target.files[0])
    }
  }

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    if (aspect) {
      const { width, height } = e.currentTarget
      setCrop(centerAspectCrop(width, height, aspect))
    } else {
      setCrop({ unit: '%', width: 90, x: 0, y: 0, height: 90 })
    }
  }

  function onCompletedCrop(e: any) {
    setCompletedCrop(e)
  }

  function onDownloadCropClick() {
    setCropEnable(false)
    if (imgRef.current && previewCanvasRef.current && completedCrop) {
      canvasPreview(
        imgRef.current,
        previewCanvasRef.current,
        completedCrop,
        1,
        0
      )
    }
    if (!previewCanvasRef.current) {
      throw new Error('Crop canvas does not exist')
    }
    const dataURL = previewCanvasRef.current.toDataURL()
    const base64 = getBase64StringFromDataURL(dataURL)
    setLoading(true)
    postFilesToServer([
      {
        type: 0,
        mime_type: 'image/png',
        size: 0,
        data: base64
      }
    ])
      .then((res) => {
        if (onChange) onChange(res?.data?.[0]?.url, res?.data?.[0])
        setIsRemove(false)
      })
      .finally(() => setLoading(false))
  }

  const getBase64StringFromDataURL = (dataURL: string) => {
    return dataURL.replace('data:', '').replace(/^.+,/, '')
  }

  const onDeleteImage = () => {
    setImgSrc('')
    setCropEnable(true)
    if (inputImgRef.current) {
      inputImgRef.current.value = ''
    }
    if (onChange) onChange(undefined, undefined)
  }

  const handleOnRemove = () => {
    if (onChange) onChange(undefined, undefined)
    setIsRemove(true)
    onDeleteImage()
  }

  const handleOnCropImg = () => {
    if (onChange) onChange(undefined, undefined)
    setCropEnable(true)
  }

  return (
    <SpinLoading loading={loading}>
      <div className={className}>
        {!isRemove && value && (
          <UploadImage
            required={required}
            value={value}
            title={title}
            buttonUpload={buttonUpload}
            preventRemove={preventRemove}
            disabled={disabled}
            onRemove={handleOnRemove}
          />
        )}
        {!value && (
          <StyledContainer>
            {title && (
              <StyledLabel>
                {title !== undefined ? title : t('attachment-img')}
                {required && <span> *</span>}
              </StyledLabel>
            )}

            <div className="Crop-Controls">
              <input
                ref={inputImgRef}
                type="file"
                accept="image/*"
                onChange={onSelectFile}
                disabled={disabled}
                style={{ display: 'none' }}
              />
              {!imgSrc && (
                <StyledButtonInputImg
                  style={{
                    display: 'block',
                    opacity: disabled ? '0.5' : '1'
                  }}
                  onClick={() => {
                    setCropEnable(true)
                    setTimeout(() => {
                      inputImgRef?.current?.click()
                    })
                  }}>
                  {buttonUpload ?? <UploadOutlined />}
                </StyledButtonInputImg>
              )}
            </div>
            {!!completedCrop && (
              <StyledWrapperPreviewImg>
                {imgSrc && (
                  <StyledButtonContainer>
                    {cropEnable ? (
                      <StyledButton title={t('save')}>
                        <Button
                          type="primary"
                          shape="circle"
                          icon={<SaveOutlined />}
                          onClick={onDownloadCropClick}
                        />
                      </StyledButton>
                    ) : (
                      <StyledButton title={t('crop-image')}>
                        <Button
                          shape="circle"
                          type="dashed"
                          icon={<ScissorOutlined />}
                          onClick={handleOnCropImg}
                        />
                      </StyledButton>
                    )}

                    <StyledButton title={t('delete')}>
                      <Button
                        shape="circle"
                        type="dashed"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={onDeleteImage}
                      />
                    </StyledButton>
                  </StyledButtonContainer>
                )}
                {imgSrc && (
                  <canvas
                    ref={previewCanvasRef}
                    style={{
                      border: '1px solid black',
                      objectFit: 'contain',
                      width: cropEnable ? 0 : completedCrop.width,
                      height: cropEnable ? 0 : completedCrop.height
                    }}
                  />
                )}
              </StyledWrapperPreviewImg>
            )}
            {!!imgSrc && cropEnable && (
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => onCompletedCrop(c)}
                aspect={aspect}>
                <img
                  ref={imgRef}
                  alt="Crop me"
                  src={imgSrc}
                  style={{ transform: `scale(${1}) rotate(${0}deg)` }}
                  onLoad={onImageLoad}
                />
              </ReactCrop>
            )}
          </StyledContainer>
        )}
      </div>
    </SpinLoading>
  )
}
